import { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Container,
  useTheme,
  useMediaQuery,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Snackbar,
  CircularProgress,
} from "@mui/material";
import { motion } from "framer-motion";
import emailjs from "@emailjs/browser";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import SendIcon from "@mui/icons-material/Send";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import MessageIcon from "@mui/icons-material/Message";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const ContactForm = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    date: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success", // 'success' or 'error'
  });

  // EmailJS Configuration - Replace with your actual credentials
  const EMAILJS_CONFIG = {
    SERVICE_ID: "service_ajpvdyp", // Replace with your EmailJS service ID
    TEMPLATE_ID: "template_db3v04q", // Replace with your EmailJS template ID
    PUBLIC_KEY: "WJeU8A92db8GXkvGO", // Replace with your EmailJS public key
  };

  const services = [
    "Post Construction Cleaning",
    "Deep Cleaning",
    "Fumigation Services",
    "Hiring Maids",
    "Vacuum Cleaning",
    "Laundry Services",
    "Office Cleaning",
    "Eco-Friendly Cleaning",
    "Training and certifying cleaners",
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate form data
      if (
        !formData.name ||
        !formData.phone ||
        !formData.service ||
        !formData.date
      ) {
        throw new Error("Please fill in all required fields");
      }

      // Prepare template parameters
      const templateParams = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        service: formData.service,
        date: formData.date,
        message: formData.message || "No additional message provided",
        to_email: "visacciallanz14@gmail.com",
        subject: `New Cleaning Service Request from ${formData.name}`,
      };

      // Send email using EmailJS
      const result = await emailjs.send(
        EMAILJS_CONFIG.SERVICE_ID,
        EMAILJS_CONFIG.TEMPLATE_ID,
        templateParams,
        EMAILJS_CONFIG.PUBLIC_KEY
      );

      console.log("Email sent successfully:", result);

      // Show success message
      setSnackbar({
        open: true,
        message:
          "Message sent successfully! We'll get back to you within 24 hours.",
        severity: "success",
      });

      // Reset form
      setFormData({
        name: "",
        email: "",
        phone: "",
        service: "",
        date: "",
        message: "",
      });
    } catch (error) {
      console.error("Error sending email:", error);

      // Show error message
      setSnackbar({
        open: true,
        message:
          error.message ||
          "Failed to send message. Please try again or contact us directly.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  return (
    <Box
      sx={{
        py: { xs: 6, md: 10 },
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        width: "100%",
      }}
    >
      <Container maxWidth="sm">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <Box
            sx={{
              background: "white",
              borderRadius: 4,
              p: { xs: 3, md: 5 },
              boxShadow: "0 20px 60px rgba(0,0,0,0.1)",
              border: "1px solid rgba(255,255,255,0.2)",
              backdropFilter: "blur(10px)",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 4,
                background:
                  "linear-gradient(90deg, #081446ff 0%, #764ba2 100%)",
              },
            }}
          >
            {/* Decorative Elements */}
            <Box
              sx={{
                position: "absolute",
                top: -50,
                right: -50,
                width: 120,
                height: 120,
                borderRadius: "50%",
                background:
                  "linear-gradient(45deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)",
              }}
            />

            <Box
              sx={{
                position: "absolute",
                bottom: -30,
                left: -30,
                width: 80,
                height: 80,
                borderRadius: "30%",
                background:
                  "linear-gradient(45deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)",
                rotate: "45deg",
              }}
            />

            <Box sx={{ position: "relative", zIndex: 2 }}>
              {/* Header */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 800,
                    textAlign: "center",
                    mb: 2,
                    background:
                      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    backgroundClip: "text",
                    WebkitBackgroundClip: "text",
                    color: "transparent",
                    fontSize: { xs: "2rem", md: "2.5rem" },
                  }}
                >
                  Get In Touch
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: "text.secondary",
                    textAlign: "center",
                    mb: 4,
                    fontWeight: 400,
                    lineHeight: 1.6,
                  }}
                >
                  Ready to transform your space? Book your cleaning service
                  today!
                </Typography>
              </motion.div>

              <motion.form
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                onSubmit={handleSubmit}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "24px",
                }}
              >
                {/* Name Field */}
                <motion.div variants={itemVariants}>
                  <TextField
                    fullWidth
                    name="name"
                    label="Full Name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    variant="outlined"
                    InputProps={{
                      startAdornment: (
                        <PersonIcon sx={{ color: "primary.main", mr: 1 }} />
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 3,
                        backgroundColor: "rgba(102, 126, 234, 0.02)",
                        "&:hover fieldset": {
                          borderColor: "primary.main",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "primary.main",
                          borderWidth: 2,
                        },
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "primary.main",
                      },
                    }}
                  />
                </motion.div>

                {/* Email Field */}
                <motion.div variants={itemVariants}>
                  <TextField
                    fullWidth
                    name="email"
                    type="email"
                    label="Email Address (Optional)"
                    value={formData.email}
                    onChange={handleChange}
                    // required
                    variant="outlined"
                    InputProps={{
                      startAdornment: (
                        <EmailIcon sx={{ color: "primary.main", mr: 1 }} />
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 3,
                        backgroundColor: "rgba(102, 126, 234, 0.02)",
                        "&:hover fieldset": {
                          borderColor: "primary.main",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "primary.main",
                          borderWidth: 2,
                        },
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "primary.main",
                      },
                    }}
                  />
                </motion.div>

                {/* Phone Field */}
                <motion.div variants={itemVariants}>
                  <TextField
                    fullWidth
                    name="phone"
                    type="tel"
                    label="Phone Number"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    variant="outlined"
                    InputProps={{
                      startAdornment: (
                        <PhoneIcon sx={{ color: "primary.main", mr: 1 }} />
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 3,
                        backgroundColor: "rgba(102, 126, 234, 0.02)",
                        "&:hover fieldset": {
                          borderColor: "primary.main",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "primary.main",
                          borderWidth: 2,
                        },
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "primary.main",
                      },
                    }}
                  />
                </motion.div>

                {/* Service Selection */}
                <motion.div variants={itemVariants}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Select Service *</InputLabel>
                    <Select
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      label="Select Service *"
                      required
                      sx={{
                        borderRadius: 3,
                        backgroundColor: "rgba(102, 126, 234, 0.02)",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "rgba(0, 0, 0, 0.23)",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "primary.main",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "primary.main",
                          borderWidth: 2,
                        },
                      }}
                    >
                      {services.map((service) => (
                        <MenuItem key={service} value={service}>
                          {service}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </motion.div>

                {/* Date Selection */}
                <motion.div variants={itemVariants}>
                  <TextField
                    fullWidth
                    name="date"
                    type="date"
                    label="Preferred Date"
                    value={formData.date}
                    onChange={handleChange}
                    required
                    variant="outlined"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    InputProps={{
                      startAdornment: (
                        <CalendarTodayIcon
                          sx={{ color: "primary.main", mr: 1 }}
                        />
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 3,
                        backgroundColor: "rgba(102, 126, 234, 0.02)",
                        "&:hover fieldset": {
                          borderColor: "primary.main",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "primary.main",
                          borderWidth: 2,
                        },
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "primary.main",
                      },
                    }}
                  />
                </motion.div>

                {/* Message Field */}
                <motion.div variants={itemVariants}>
                  <TextField
                    fullWidth
                    name="message"
                    label="Additional Message (Optional)"
                    value={formData.message}
                    onChange={handleChange}
                    multiline
                    rows={4}
                    variant="outlined"
                    InputProps={{
                      startAdornment: (
                        <MessageIcon
                          sx={{
                            color: "primary.main",
                            mr: 1,
                            mt: 1,
                            alignSelf: "flex-start",
                          }}
                        />
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 3,
                        backgroundColor: "rgba(102, 126, 234, 0.02)",
                        "&:hover fieldset": {
                          borderColor: "primary.main",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "primary.main",
                          borderWidth: 2,
                        },
                        alignItems: "flex-start",
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "primary.main",
                      },
                    }}
                  />
                </motion.div>

                {/* Submit Button */}
                <motion.div
                  variants={itemVariants}
                  whileHover={{ scale: loading ? 1 : 1.02 }}
                  whileTap={{ scale: loading ? 1 : 0.98 }}
                >
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={loading}
                    endIcon={
                      loading ? <CircularProgress size={20} /> : <SendIcon />
                    }
                    sx={{
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      color: "white",
                      fontWeight: 700,
                      py: 2,
                      borderRadius: 3,
                      textTransform: "none",
                      fontSize: "1.1rem",
                      boxShadow: loading
                        ? "none"
                        : "0 8px 25px rgba(102, 126, 234, 0.3)",
                      "&:hover": {
                        background: loading
                          ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                          : "linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)",
                        boxShadow: loading
                          ? "none"
                          : "0 12px 35px rgba(102, 126, 234, 0.4)",
                        transform: loading ? "none" : "translateY(-2px)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    {loading ? "Sending..." : "Book Your Service"}
                  </Button>
                </motion.div>
              </motion.form>

              {/* Footer Note */}
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.8 }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    textAlign: "center",
                    color: "text.secondary",
                    mt: 3,
                    fontSize: "0.875rem",
                  }}
                >
                  We'll get back to you within 24 hours to confirm your booking
                </Typography>
              </motion.div>
            </Box>
          </Box>
        </motion.div>
      </Container>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
          icon={snackbar.severity === "success" ? <CheckCircleIcon /> : null}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContactForm;
